import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, getCurrentInstance, computed, set } from 'vue';

import { AccountEmailDomainPayload, AccountEmailDomainResponse } from '@/api/account_email_domains';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const.js';
import { useAccountEmailDomainActions, useAccountEmailDomainGetters } from '@/store-composables';
import ErrorHandlingService from '@/utils/error-handling-service';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';


export default /*#__PURE__*/_defineComponent({
  __name: 'EmailDomainsTab',
  setup(__props) {

const billingFeatureId = ref(FeatureTypes.CustomerCustomEmailDomains);
const { fetchAccEmailDomains, addAccEmailDomain, verifyDkim, deleteEmailDomain, updateEmailDomain } = useAccountEmailDomainActions();
const { useAccountEmailDomainsArr: accEmailDomainsArr } = useAccountEmailDomainGetters();
const loadingState = ref<Record<string, boolean>>({});
function updateLoadingState(id: number, state: boolean) {
	set(loadingState.value, id, state);
}

const toast = getCurrentInstance()?.proxy?.$toast || console;

// Fetch account email domans from api
onMounted(async () => {
	await fetchAccEmailDomains();
});

const tableItems = computed(() => accEmailDomainsArr.value.filter((item) => !item.is_ply_mail));

const deleteDomain = async (item: AccountEmailDomainResponse) => {
	const res = await AppDialogManagerService.openConfirmationDialog({
		title: 'Delete domain',
		description: `Are you sure you want to delete ${item.domain}?`,
		yesText: 'Delete',
		noText: 'Cancel',
	});
	if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
		try {
			await deleteEmailDomain(item.id);
		} catch (e) {
			toast.error(`Failed to delete domain, ${ErrorHandlingService.extractErrorMessage(e)}`);
		}
	}
};

const handleDomainInfoDialog = async (domain: AccountEmailDomainResponse) => {
	const dialogRes = await AppDialogManagerService.openEmailDomainInfoDialog({ domain });
	if (!dialogRes || !dialogRes.domainId) {
		return;
	}
	await verifyDkim(dialogRes.domainId);
};

const openAddAllowedEmailsToDomain = async (domain: AccountEmailDomainResponse) => {
	const dialogRes = await AppDialogManagerService.openAddAllowedEmailsToDomain({ domain });
	if (!dialogRes) {
		return;
	}
	if (dialogRes.emails) {
		try {
			await updateEmailDomain({ id: domain.id, list_of_allowed_emails: dialogRes.emails });
		} catch (error) {
			toast.error(ErrorHandlingService.extractErrorMessage(error) || 'Failed to update domain');
		}
	} else if (dialogRes.openInfo) {
		await handleDomainInfoDialog(domain);
	}
};

const openEmailDomainInfoOrAllowedEmailsDialog = async (domain: AccountEmailDomainResponse) => {
	if (domain.verified) {
		await openAddAllowedEmailsToDomain(domain);
	} else {
		await handleDomainInfoDialog(domain);
	}
};
const openAddDomainDialog = async (event: Event, isUpgradeRequired: boolean, indicator) => {
	/* domain: AccountEmailDomainResponse, looks like wasn't used */
	if (isUpgradeRequired && indicator?.setup) {
		indicator.setup().openUpgrade(event, billingFeatureId.value);
	} else {
		const dialogRes = await AppDialogManagerService.openAddEmailDomainDialog();
		if (!dialogRes) {
			return;
		}
		const { domainName, returnPathName } = dialogRes;
		if (domainName) {
			const newDomainRes: AccountEmailDomainResponse | void = await addDomainClicked(domainName, returnPathName);
			if (!newDomainRes) {
				throw new Error('Failed to add domain');
			}
			await handleDomainInfoDialog(newDomainRes);
		} else {
			toast.error('Domain name & allowed email fields are required');
		}
	}
};

const domainSearchInput = ref('');

// Manage items currently not used, remove if not needed // TODO remove
const manageItems = [
	{ title: 'Show All', value: 0 },
	{ title: 'Active', value: 1 },
	{ title: 'Inactive', value: 2 },
	{ title: 'Invite', value: 3 },
];
const manageItemsMap: Record<number, string> = {};
manageItems.forEach((item) => {
	manageItemsMap[item.value] = item.title;
});

const verifyDomain = async (domainId: number) => {
	try {
		updateLoadingState(domainId, true);
		const domainResp: AccountEmailDomainResponse = await verifyDkim(domainId);
		if (domainResp.verified) {
			fetchAccEmailDomains();
		}
	} catch (error: unknown) {
		toast.error(ErrorHandlingService.extractErrorMessage(error) || 'Failed to verify domain');
	} finally {
		updateLoadingState(domainId, false);
	}
};

const tableHeaders = [
	{ text: 'Domain', value: 'domain' },
	{ text: 'Allowed addresses', value: 'list_of_allowed_emails' },
	{ text: 'Status', value: 'verified' },
	{ text: 'Verify', value: 'verify' },
	{ text: '', value: 'actions' },
];
const loadingEmailDomains = ref(false);

async function addDomainClicked(domainName: string, returnPathName: string): Promise<AccountEmailDomainResponse | void> {
	const payload: AccountEmailDomainPayload = {
		domain: domainName,
		list_of_allowed_emails: [],
	};
	if (!isNullOrUndefinedOrEmpty(returnPathName)) {
		payload.return_path_domain = returnPathName;
	}
	try {
		return await addAccEmailDomain(payload);
	} catch (error) {
		toast.error('Failed to add domain');
	}
}

return { __sfc: true,billingFeatureId, fetchAccEmailDomains, addAccEmailDomain, verifyDkim, deleteEmailDomain, updateEmailDomain, accEmailDomainsArr, loadingState, updateLoadingState, toast, tableItems, deleteDomain, handleDomainInfoDialog, openAddAllowedEmailsToDomain, openEmailDomainInfoOrAllowedEmailsDialog, openAddDomainDialog, domainSearchInput, manageItems, manageItemsMap, verifyDomain, tableHeaders, loadingEmailDomains, addDomainClicked, MaybeUpgradeRequiredIndicator }
}

})